import type { ComponentPropsWithoutRef, MouseEvent, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import type { ButtonSize, ButtonTone, ButtonVariant } from './types';
import { classes, getSpinnerSize, getToneGradient } from './utils';

export type { ButtonVariant, ButtonTone, ButtonSize } from './types';

export interface ButtonProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'ref'
> {
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
}

export default function Button({
  variant = 'primary',
  tone = 'brand',
  size = 'md',
  disabled = false,
  loading = false,
  type = 'button',
  icon,
  className,
  children,
  ...props
}: ButtonProps) {
  const inactive = disabled || loading;
  return (
    <button
      disabled={inactive}
      className={twMerge(
        classes.base,
        classes[size],
        classes[variant],
        className
      )}
      type={type}
      {...props}>
      {variant === 'primary' && (
        <span
          className={twMerge(
            classes.gradientLayer,
            getToneGradient(tone),
            inactive && classes.gradientHidden
          )}
        />
      )}
      <span className={classes.content}>
        {loading && (
          <span className={twMerge(classes.spinner, getSpinnerSize(size))} />
        )}
        {!loading && icon}
        {children}
        {loading && <LoopingEllipsis />}
      </span>
    </button>
  );
}
